/**
 * B4 · G3 — tarjeta «Adelanta y ahorra».
 *
 * Regla de negocio del dueño: el alumno cursó y pagó el primer mes, y entonces
 * se le ofrece pagar por adelantado los meses restantes con un porcentaje de
 * descuento. El motivo declarado es el flujo de caja.
 *
 * Tres decisiones de producto que explican el diseño:
 *
 *  1. **El ahorro se muestra en USD Y en VES.** El compromiso es en USD (no se
 *     mueve si el BCV cambia mañana), pero el alumno razona en bolívares: una
 *     tarjeta que solo diga «$22,50» no convence a nadie en Venezuela. La línea
 *     en VES lleva la marca de indicativa, no una promesa.
 *
 *  2. **Cuando NO hay oferta, la tarjeta explica qué falta** en vez de
 *     desaparecer. «Paga tu primera cuota y desbloqueas un 10 %» es la propia
 *     condición del negocio convertida en incentivo; ocultar la tarjeta
 *     desperdicia el momento en que el alumno está justamente mirando su deuda.
 *
 *  3. **Se manda el importe que el alumno tenía delante.** Si alguien le abona
 *     una cuota entre que ve la oferta y pulsa, el servidor rechaza en vez de
 *     cobrarle un importe distinto del que aprobó.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { PiggyBank, Sparkles } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { Money } from "./money";
import { type PrepayOffer, useAcceptPrepay, useMePrepayOffers } from "../api";

const BP_PER_PERCENT = 100;

/** `1050` → `"10,5"`. Se formatea con el locale del usuario. */
const percentLabel = (percentBp: number, locale: string): string =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(percentBp / BP_PER_PERCENT);

export const PrepayCard: Component<{ vesPerUsd?: number | null }> = (props) => {
  const { t, locale } = useI18n();
  // La oferta es una feature de la academia: si está apagada, ni se pide.
  const enabled = useFeatureEnabled(() => "billing.prepay");
  const offers = useMePrepayOffers();
  const accept = useAcceptPrepay();
  const [pending, setPending] = createSignal<string | null>(null);

  // Solo interesa la mejor oferta viva; el resto de matrículas se listan como
  // «aún no» con su motivo, sin competir por la atención.
  const eligible = createMemo(() =>
    (offers.data ?? []).filter((o) => o.eligibility === "eligible" && o.quote),
  );
  const blocked = createMemo(() => (offers.data ?? []).filter((o) => o.eligibility !== "eligible"));

  const reasonLabel = (o: PrepayOffer): string => {
    switch (o.eligibility) {
      case "first_installment_unpaid": {
        return t(msg`Pay your first instalment and this discount unlocks.`);
      }
      case "not_enough_installments": {
        return t(msg`There aren't enough upcoming instalments left to bring forward.`);
      }
      case "already_prepaid": {
        return t(msg`You already brought this enrolment forward. Nothing else is due.`);
      }
      default: {
        return "";
      }
    }
  };

  const onAccept = (o: PrepayOffer) => {
    const { quote } = o;
    if (!quote) {
      return;
    }
    setPending(o.enrollmentId);
    accept.mutate(
      {
        enrollmentId: o.enrollmentId,
        // Anti-sorpresa: el servidor compara con la oferta viva y rechaza si
        // cambió mientras el alumno decidía.
        expectedTotalUsdMinor: quote.totalUsdMinor,
        expectedDiscountUsdMinor: quote.discountUsdMinor,
      },
      {
        onSuccess: () => {
          setPending(null);
          showToast(t(msg`Done: your remaining instalments are now a single invoice.`), "success");
        },
        onError: (e) => {
          setPending(null);
          showToast(e.message, "error");
        },
      },
    );
  };

  return (
    <Show when={enabled().enabled}>
      <Show when={!offers.isLoading} fallback={<Skeleton class="h-28 w-full" />}>
        <Show when={(offers.data?.length ?? 0) > 0}>
          <div class="space-y-3" data-testid="prepay-card">
            <For each={eligible()}>
              {(o) => (
                <div
                  class="rounded-xl border border-primary/40 bg-primary/5 p-4"
                  data-testid={`prepay-offer-${o.enrollmentId}`}
                >
                  <div class="flex items-center gap-2">
                    <Sparkles class="h-4 w-4 text-primary" />
                    <h3 class="font-semibold">{t(msg`Pay ahead and save`)}</h3>
                    <span
                      class="ml-auto rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground"
                      data-testid="prepay-percent"
                    >
                      −{percentLabel(o.quote?.percentBp ?? 0, locale())}%
                    </span>
                  </div>
                  <p class="mt-2 text-sm text-muted-foreground">
                    {(() => {
                      const count = o.quote?.installmentCount ?? 0;
                      return t(msg`Settle your ${count} remaining instalments in one payment.`);
                    })()}
                  </p>
                  <dl class="mt-3 space-y-1 text-sm">
                    <div class="flex items-center justify-between">
                      <dt class="text-muted-foreground">{t(msg`Month by month`)}</dt>
                      <dd class="line-through opacity-60">
                        <Money
                          usdMinor={o.quote?.baseUsdMinor ?? 0}
                          vesMinor={o.baseVesMinor}
                          showRate={false}
                        />
                      </dd>
                    </div>
                    <div class="flex items-center justify-between">
                      <dt class="font-medium">{t(msg`Paying ahead`)}</dt>
                      <dd data-testid="prepay-total">
                        <Money
                          usdMinor={o.quote?.totalUsdMinor ?? 0}
                          vesMinor={o.totalVesMinor}
                          showRate={false}
                        />
                      </dd>
                    </div>
                    <div class="flex items-center justify-between text-emerald-700 dark:text-emerald-400">
                      <dt class="flex items-center gap-1 font-semibold">
                        <PiggyBank class="h-4 w-4" />
                        {t(msg`You save`)}
                      </dt>
                      <dd class="font-semibold" data-testid="prepay-savings">
                        <Money
                          usdMinor={o.quote?.discountUsdMinor ?? 0}
                          vesMinor={o.savingsVesMinor}
                          showRate={false}
                        />
                      </dd>
                    </div>
                  </dl>
                  {/* El compromiso es en USD: la conversión es referencial. */}
                  <Show when={props.vesPerUsd}>
                    <p class="mt-2 text-xs text-muted-foreground">
                      {t(msg`The amount is fixed in USD; the bolívar figure follows today's rate.`)}
                    </p>
                  </Show>
                  <Button
                    class="mt-3 w-full"
                    data-testid={`prepay-accept-${o.enrollmentId}`}
                    disabled={pending() === o.enrollmentId}
                    onClick={() => {
                      onAccept(o);
                    }}
                  >
                    {t(msg`Bring my instalments forward`)}
                  </Button>
                </div>
              )}
            </For>

            {/* Sin oferta todavía: se dice QUÉ falta, que es el propio gancho. */}
            <For each={blocked()}>
              {(o) => (
                <div
                  class="rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground"
                  data-testid={`prepay-blocked-${o.enrollmentId}`}
                >
                  <span class="font-medium text-foreground">{t(msg`Pay ahead and save`)}</span>
                  <span> · {reasonLabel(o)}</span>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </Show>
  );
};
